import { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Card,
  Label,
  TextInput,
  Button,
  Progress,
  Tooltip,
  useThemeMode
} from "flowbite-react";
import { HiSearch, HiOutlineInformationCircle } from "react-icons/hi";
import { useLazyGetChannelDistributionReportQuery } from "../../api/reportsApi/reports";
import { useReportCompanyParam } from "../../hooks/useReportCompanyParam";
import DateRangePresets from "../../components/reports/DateRangePresets";
import { getErrorMessage } from "../../utils/requestUtils";
import { toast } from "@/lib/toast";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";

const ChannelDistributionReport: FC = () => {
  const { t } = useTranslation(["reports"]);
  const { mode } = useThemeMode();
  const isDark = mode === "dark";

  const today = new Date().toISOString().split("T")[0];

  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  
  const [triggerReport, { data: reportData, isLoading }] = useLazyGetChannelDistributionReportQuery();
  const companyParam = useReportCompanyParam();

  const handleSearch = async () => {
    if (!startDate || !endDate) {
      toast.warn(t("selectClientDateWarning"));
      return;
    }

    if (startDate > endDate) {
      toast.error(t("startDateHigherThanEndDate"));
      return;
    }

    try {
      await triggerReport({
        start_date: startDate,
        end_date: endDate,
        ...companyParam
      }).unwrap();
    } catch (error: any) {
       toast.error(getErrorMessage(error));
    }
  };

  // Widget funnel: the three outcomes are mutually exclusive and add up to
  // the session total, so the donut's own percentages match the rate bars.
  // Every figure prints its unit. Conversation counts and ticket counts share
  // this page and look interchangeable when both are bare numbers, which is
  // the single thing readers get wrong about this report.
  const summaryCards = [
    { label: t("summary.totalSessions"), hint: t("hints.totalSessions"), value: reportData?.summary.total_widget_sessions ?? 0, color: "text-blue-600" },
    { label: t("summary.resolvedByAi"), hint: t("hints.resolvedByAi"), value: reportData?.summary.resolved_by_ai ?? 0, color: "text-green-600" },
    { label: t("summary.ticketsCreated"), hint: t("hints.ticketsCreated"), value: reportData?.summary.ended_with_ticket ?? 0, color: "text-purple-600" },
    { label: t("summary.transferredToLive"), hint: t("hints.transferredToLive"), value: reportData?.summary.transferred_to_live_agent ?? 0, color: "text-orange-600" },
    { label: t("summary.noEngagement"), hint: t("hints.noEngagement"), value: reportData?.summary.no_engagement ?? 0, color: "text-gray-500" },
  ];

  const donutBase = (labels: string[], colors: string[]): ApexOptions => ({
    chart: {
      type: "donut",
      background: "transparent",
    },
    labels,
    colors,
    legend: {
      position: "bottom",
      labels: {
        colors: isDark ? "#fff" : "#000",
      }
    },
    dataLabels: {
      enabled: true,
    },
    theme: {
      mode: isDark ? 'dark' : 'light',
    }
  });

  const sessionChartOptions = donutBase(
    [
      t("summary.resolvedByAi"),
      t("summary.ticketsCreated"),
      t("summary.transferredToLive"),
      t("summary.noEngagement"),
    ],
    ["#16bdca", "#7e3af2", "#fdba8c", "#9ca3af"]
  );

  const sessionChartSeries = [
    reportData?.summary.resolved_by_ai ?? 0,
    reportData?.summary.ended_with_ticket ?? 0,
    reportData?.summary.transferred_to_live_agent ?? 0,
    reportData?.summary.no_engagement ?? 0,
  ];

  // Ticket origin is a separate dimension from the session funnel — it is
  // charted on its own so ticket counts are never read as session counts.
  const ticketChannels = useMemo(
    () => (reportData?.tickets.by_channel ?? []).filter((c) => c.count > 0),
    [reportData]
  );

  const ticketChannelLabel = (label: string): string =>
    t(`ticketChannels.${label}`, { defaultValue: label });

  const ticketChartOptions = donutBase(
    ticketChannels.map((c) => ticketChannelLabel(c.label)),
    ["#1a56db", "#e3a008", "#16bdca", "#9061f9", "#6b7280"]
  );

  const ticketChartSeries = ticketChannels.map((c) => c.count);

  const ticketsTotal = reportData?.tickets.total ?? 0;

  // The two sections count different things — conversations above, tickets
  // below — which is the single most common way to misread this page, so every
  // figure carries its own definition rather than relying on the label.
  const InfoHint: FC<{ text: string }> = ({ text }) => (
    <Tooltip content={<span className="block max-w-xs text-xs leading-relaxed">{text}</span>}>
      <HiOutlineInformationCircle
        className="h-4 w-4 shrink-0 cursor-help text-gray-400 dark:text-gray-500"
        aria-label={text}
      />
    </Tooltip>
  );

  return (
    <div className="w-full">
      <div className="px-4 pt-6">
        <Card className="mb-6">
            <DateRangePresets
              className="mb-4"
              startDate={startDate}
              endDate={endDate}
              onChange={(s, e) => { setStartDate(s); setEndDate(e); }}
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                 <div>
                    <div className="mb-2 block">
                        <Label htmlFor="startDate" value={t("startDate")} />
                    </div>
                    <TextInput
                        id="startDate"
                        type="date"
                        value={startDate}
                        max={today}
                        onChange={(e) => setStartDate(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key !== "Tab") e.preventDefault();
                        }}
                        required
                    />
                </div>
                 <div>
                    <div className="mb-2 block">
                        <Label htmlFor="endDate" value={t("endDate")} />
                    </div>
                    <TextInput
                        id="endDate"
                        type="date"
                        value={endDate}
                        max={today}
                        onChange={(e) => setEndDate(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key !== "Tab") e.preventDefault();
                        }}
                        required
                    />
                </div>
                <Button color="primary" onClick={handleSearch} disabled={isLoading} isProcessing={isLoading}>
                    <HiSearch className="mr-2 h-5 w-5" />
                    {t("generateReport")}
                </Button>
            </div>
        </Card>

        {reportData && (
            <div className="grid gap-4">
                <div>
                    <h4 className="flex items-center gap-1.5 text-lg font-bold text-gray-900 dark:text-white">
                        <span>{t("charts.widgetFunnel")}</span>
                        <InfoHint text={t("hints.widgetFunnel")} />
                    </h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t("charts.widgetFunnelSubtitle")}</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                    {summaryCards.map((card, index) => (
                        <Card key={index}>
                            <h5 className="flex items-center gap-1.5 text-sm tracking-tight text-gray-500 dark:text-gray-400">
                                <span>{card.label}</span>
                                <InfoHint text={card.hint} />
                            </h5>
                            <p className={`text-2xl font-bold ${card.color} dark:text-white`}>
                                {card.value}
                                <span className="ml-1 text-xs font-normal text-gray-500 dark:text-gray-400">
                                    {t("units.conversations")}
                                </span>
                            </p>
                        </Card>
                    ))}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                     <Card>
                        <h5 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                            {t("charts.sessionDistribution")}
                        </h5>
                        <div className="h-80">
                            <ReactApexChart 
                                options={sessionChartOptions}
                                series={sessionChartSeries}
                                type="donut" 
                                height="100%" 
                            />
                        </div>
                    </Card>
                    <Card>
                        <h5 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                            {t("charts.performanceRates")}
                        </h5>
                        <div className="space-y-6">
                            <div>
                                <div className="mb-1 flex justify-between font-medium dark:text-white">
                                    <span>{t("charts.aiResolutionRate")}</span>
                                    <span>{reportData.percentages.ai_resolution_rate}%</span>
                                </div>
                                <Progress progress={reportData.percentages.ai_resolution_rate} color="teal" size="lg" />
                            </div>
                            <div>
                                <div className="mb-1 flex justify-between font-medium dark:text-white">
                                    <span>{t("charts.ticketCreationRate")}</span>
                                    <span>{reportData.percentages.ticket_rate}%</span>
                                </div>
                                <Progress progress={reportData.percentages.ticket_rate} color="purple" size="lg" />
                            </div>
                             <div>
                                <div className="mb-1 flex justify-between font-medium dark:text-white">
                                    <span>{t("charts.liveTransferRate")}</span>
                                    <span>{reportData.percentages.live_agent_rate}%</span>
                                </div>
                                <Progress progress={reportData.percentages.live_agent_rate} color="blue" size="lg" />
                            </div>
                        </div>
                        <div className="mt-4 space-y-1 text-xs text-gray-500 dark:text-gray-400">
                            <p>{t("charts.ticketAfterLiveNote", { count: reportData.escalation_detail.ticket_after_live })}</p>
                            <p>{t("charts.noEngagementNote", { count: reportData.summary.no_engagement })}</p>
                            <p>
                                {t("charts.escalationDetail", {
                                    sameSession: reportData.escalation_detail.same_session,
                                    followUp: reportData.escalation_detail.follow_up,
                                })}
                            </p>
                        </div>
                     </Card>
                </div>

                <div className="mt-4">
                    <h4 className="flex items-center gap-1.5 text-lg font-bold text-gray-900 dark:text-white">
                        <span>{t("charts.ticketChannels")}</span>
                        <InfoHint text={t("hints.ticketChannels")} />
                    </h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t("charts.ticketChannelsSubtitle")}</p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <Card>
                        <h5 className="text-sm tracking-tight text-gray-500 dark:text-gray-400">
                            {t("ticketChannels.total")}
                        </h5>
                        <p className="text-2xl font-bold text-blue-600 dark:text-white">
                            {ticketsTotal}
                            <span className="ml-1 text-xs font-normal text-gray-500 dark:text-gray-400">
                                {t("units.tickets")}
                            </span>
                        </p>
                        <div className="mt-4 space-y-4">
                            {ticketChannels.map((channel) => {
                                const percentage = ticketsTotal > 0
                                    ? Math.round((channel.count / ticketsTotal) * 1000) / 10
                                    : 0;
                                return (
                                    <div key={channel.channel}>
                                        <div className="mb-1 flex justify-between text-sm font-medium dark:text-white">
                                            <span>{ticketChannelLabel(channel.label)}</span>
                                            <span>
                                                {channel.count} {t("units.tickets")} ({percentage}%)
                                            </span>
                                        </div>
                                        <Progress progress={percentage} color="blue" size="sm" />
                                        {channel.label === "aiagent" && (
                                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                                {t("charts.widgetTicketSplit", {
                                                    matched: reportData.tickets.from_widget_matched,
                                                    unmatched: reportData.tickets.from_widget_unmatched,
                                                })}
                                            </p>
                                        )}
                                    </div>
                                );
                            })}
                            {ticketChannels.length === 0 && (
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    {t("noDataMessage")}
                                </p>
                            )}
                        </div>
                    </Card>
                    <Card>
                        <h5 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                            {t("charts.ticketChannelDistribution")}
                        </h5>
                        <div className="h-80">
                            {ticketChannels.length > 0 ? (
                                <ReactApexChart
                                    options={ticketChartOptions}
                                    series={ticketChartSeries}
                                    type="donut"
                                    height="100%"
                                />
                            ) : (
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    {t("noDataMessage")}
                                </p>
                            )}
                        </div>
                    </Card>
                </div>
            </div>
        )}
      </div>
    </div>
  );
};

export default ChannelDistributionReport;
